import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { langColor } from '../utils/markdownRenderer';
import { useAuth } from '../hooks/useAuth';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

const PRESETS = [
  { icon: '⚡', label: 'Write Function', prompt: 'Write a TypeScript function that ' },
  { icon: '🐛', label: 'Debug Code', prompt: 'Find and fix bugs in this code:\n\n```\n\n```' },
  { icon: '🏗️', label: 'Architecture', prompt: 'Design a scalable microservices architecture for ' },
];

interface ChatSession {
  id: string;
  prompt: string;
  mode: string; // The mode under which this session was created
  state: string; // INIT, STREAMING, DONE, FAILED
  pipelineState: string;
  planStream: string;
  codeStream: string;
  finalResult: string;
  streamText: string;  // live streaming text (in-progress)
  error?: string;
}

function getToken(): string | null { return localStorage.getItem('eric_ai_token'); }

const MODES = [
  { id: 'basic', label: '🤖 Chat = Ask anything', desc: '', tooltip: 'Chế độ trò chuyện mặc định, nhanh chóng giải đáp các câu hỏi thông thường.\nVí dụ: "Tóm tắt bài báo này giúp tôi", "Viết email xin nghỉ phép".' },
  { id: 'pro', label: '🧠 Pro Mode', desc: '', tooltip: 'Chế độ Phân Tích Chuyên Sâu: Chuỗi suy luận đa tầng (Gemini Search -> DeepSeek-V4-Pro Blueprint -> GPT-OSS 120B Synthesis) giải quyết các bài toán phức tạp.\nLệnh thủ công: /pro [nội dung]' },
  { id: 'investigate', label: '📰 Investigate News', desc: '', tooltip: 'Trợ lý Tình báo Thị trường: Tìm kiếm và trích xuất tin tức, giá cả, thông tin mới nhất trên toàn mạng.\nVí dụ: "Giá vàng hôm nay thế nào?", "Tóm tắt tin tức công nghệ tuần này".\nLệnh thủ công: /investigate-news [nội dung]' },
  { id: 'research', label: '🔬 Deep Research', desc: '', tooltip: 'Chuyên gia Đa miền: Tìm kiếm chuyên sâu, cào dữ liệu từ nhiều nguồn (PDF, DOCX...) và Audit bằng AI miễn phí.\nVí dụ: "Tìm hiểu chi tiết về Luật Doanh Nghiệp 2020 filetype:pdf".\nLệnh thủ công: /deep-research [nội dung]' },
  { id: 'document', label: '✍️ Text Expert', desc: '', tooltip: 'Soạn thảo văn bản chuẩn quốc tế (Document Block) để lưu thành Word, PDF.\n- /document [nội dung]: Viết văn bản mới.\n- /eric-wow [copy nội dung] [sắc thái]: Tăng cường sức mạnh ngôn từ với sắc thái (Mạnh mẽ / Gây áp lực / Tình cảm / Hài hoà).' }
];

// ── Code Block ────────────────────────────────────────────────

function CodeBlock({ code, lang, onSendToDocument }: { code: string; lang: string; onSendToDocument?: (content: string, isWow?: boolean) => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadFile = () => {
    const ext: Record<string, string> = {
      typescript: 'ts', javascript: 'js', python: 'py', rust: 'rs',
      go: 'go', sql: 'sql', bash: 'sh', css: 'css', html: 'html', json: 'json',
    };
    const filename = `code.${ext[lang] ?? lang ?? 'txt'}`;
    const blob = new Blob([code], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  return (
    <div style={{
      borderRadius: '10px',
      overflow: 'hidden',
      border: '1px solid rgba(255,255,255,0.08)',
      margin: '8px 0',
      background: '#0d1117',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 12px', background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <span style={{
          fontSize: '11px', fontWeight: 600, fontFamily: 'var(--font-mono)',
          color: langColor(lang), textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>{lang || 'code'}</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={downloadFile} style={btnStyle} title="Download file">↓</button>
          <button onClick={copy} style={{ ...btnStyle, color: copied ? '#10b981' : undefined }}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
          {onSendToDocument && (
            <button onClick={() => onSendToDocument(code, true)} style={{ ...btnStyle, color: 'var(--accent)' }} title="Chuyển sang Wow Mode (Deep Expert)">
              ⚡ /eric-wow
            </button>
          )}
        </div>
      </div>
      <pre style={{
        margin: 0, padding: '14px 16px', fontSize: '13px', lineHeight: 1.65,
        fontFamily: 'var(--font-mono)', overflowX: 'auto', color: '#e6edf3', whiteSpace: 'pre',
      }}><code>{code}</code></pre>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  fontSize: '11px', padding: '3px 8px', borderRadius: '5px',
  border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)',
  color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-mono)',
};

// ── Document Block ────────────────────────────────────────────

function DocumentBlock({ code, lang, onSendToDocument }: { code: string; lang: string; onSendToDocument?: (content: string, isWow?: boolean) => void }) {
  const [copied, setCopied] = useState(false);

  const cleanCode = useMemo(() => {
    if (!code) return '';
    return code
      // Ép dòng lên cùng với list item (1., -, *, +) nếu AI gõ nhầm Enter
      .replace(/^([ \t]*(?:\d+\.|-|\*|\+))[ \t]*\n+[ \t]*/gm, '$1 ')
      // Ép 3 dòng trống trở lên thành 2 dòng (tương đương 1 khoảng trống giữa các đoạn)
      .replace(/\n{3,}/g, '\n\n');
  }, [code]);

  const copy = async () => {
    await navigator.clipboard.writeText(cleanCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadWord = () => {
    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'></head><body>${cleanCode.replace(/\n/g, '<br/>')}</body></html>`;
    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'Erix_Document.doc';
    a.click();
  };

  const downloadExcel = () => {
    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body>${cleanCode}</body></html>`;
    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'Erix_Data.xls';
    a.click();
  };

  const downloadPDF = () => {
    const printWindow = window.open('', '', 'width=800,height=900');
    if (printWindow) {
      printWindow.document.write(`<html><head><title>Erix Document</title><style>body{font-family:sans-serif;line-height:1.6;padding:40px;color:#0f172a;}</style></head><body>${cleanCode.replace(/\n/g, '<br/>')}</body></html>`);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const isExcel = lang === 'excel';

  return (
    <div style={{
      borderRadius: '8px',
      overflow: 'hidden',
      border: '1px solid #e2e8f0',
      margin: '16px 0',
      background: '#ffffff',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px', background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
      }}>
        <span style={{
          fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-sans)',
          color: '#334155', display: 'flex', alignItems: 'center', gap: '6px'
        }}>
          {isExcel ? '📊 Bảng dữ liệu (Excel)' : '📄 Văn bản (Word/PDF)'}
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={copy} style={docBtnStyle} title="Copy to clipboard">
            {copied ? '✅ Đã Copy' : '📋 Copy'}
          </button>
          {!isExcel && <button onClick={downloadWord} style={docBtnStyle} title="Save as Word">💾 Word</button>}
          {isExcel && <button onClick={downloadExcel} style={docBtnStyle} title="Save as Excel">💾 Excel</button>}
          <button onClick={downloadPDF} style={docBtnStyle} title="Save as PDF">💾 PDF</button>
          {onSendToDocument && (
            <button onClick={() => onSendToDocument(code, true)} style={{...docBtnStyle, color: 'var(--accent)'}} title="Chuyển sang Wow Mode (Deep Expert) để tinh chỉnh chuyên sâu">
              ⚡ /eric-wow
            </button>
          )}
        </div>
      </div>
      <div className="markdown-body" style={{
        padding: '24px 32px', fontSize: '15px', lineHeight: 1.8,
        fontFamily: 'var(--font-sans)', color: '#0f172a',
        maxHeight: '600px', overflowY: 'auto'
      }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({node, ...props}) => <p style={{margin: '0 0 0.5em 0', whiteSpace: 'pre-wrap'}} {...props} />,
            ul: ({node, ...props}) => <ul style={{margin: '0 0 1em 0', paddingLeft: '1.5em'}} {...props} />,
            ol: ({node, ...props}) => <ol style={{margin: '0 0 1em 0', paddingLeft: '1.5em'}} {...props} />,
            li: ({node, ...props}) => <li style={{margin: '0 0 0.25em 0', whiteSpace: 'pre-wrap'}} {...props} />,
            table: ({node, ...props}) => <div style={{overflowX: 'auto', margin: '16px 0'}}><table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px', border: '1px solid #cbd5e1'}} {...props} /></div>,
            th: ({node, ...props}) => <th style={{border: '1px solid #cbd5e1', padding: '10px 14px', background: '#f1f5f9', fontWeight: 600, textAlign: 'left', color: '#1e293b'}} {...props} />,
            td: ({node, ...props}) => <td style={{border: '1px solid #cbd5e1', padding: '10px 14px', color: '#334155'}} {...props} />
          }}
        >
          {cleanCode}
        </ReactMarkdown>
      </div>
    </div>
  );
}

const docBtnStyle: React.CSSProperties = {
  fontSize: '12px', padding: '4px 10px', borderRadius: '6px',
  border: '1px solid #cbd5e1', background: '#ffffff',
  color: '#475569', cursor: 'pointer', fontFamily: 'var(--font-sans)',
  fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px',
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
};

// ── Session Bubble ────────────────────────────────────────────

const AnimatedWaiting = ({ mode }: { mode?: string }) => {
  const [dots, setDots] = useState('');
  const [msgIdx, setMsgIdx] = useState(0);

  const messagesByMode: Record<string, string[]> = {
    pro: [
      '🔍 [Bước 1/3] Gemini Flash Grounding đang quét dữ liệu thời gian thực...',
      '🧠 [Bước 2/3] DeepSeek-V4-Pro đang phân tích First Principles và chiết xuất Blueprint...',
      '📐 Đang bóc tách 5 chiều kích: Pháp lý, Tài chính, Quy trình, Bẫy ẩn & Rủi ro...',
      '✨ [Bước 3/3] GPT-OSS 120B đang chế bản và tổng hợp giải pháp chuyên sâu...',
      '🛡️ Background Auditor đang thẩm định an toàn tri thức dài hạn...',
    ],
    investigate: [
      '🔍 Tình báo AI đang quét nguồn tin tức...',
      '📡 Đang thu thập dữ liệu thời gian thực...',
      '⚡ Đang phân tích và tổng hợp thông tin...',
      '📊 Đang kiểm tra độ chính xác của dữ liệu...',
      '✨ Đang chuẩn bị kết quả cho bạn...',
    ],
    research: [
      '🔬 Chuyên gia đa miền đang phân tích chuyên sâu...',
      '📚 Đang tra cứu nhiều nguồn tài liệu...',
      '🧠 Đang tổng hợp và kiểm chứng thông tin...',
      '🔗 Đang xác minh các nguồn tham khảo...',
      '📝 Đang soạn báo cáo chuyên sâu cho bạn...',
    ],
    document: [
      '✍️ Chuyên gia soạn thảo đang định hình văn bản...',
      '📄 Đang cấu trúc nội dung theo chuẩn quốc tế...',
      '🎯 Đang tinh chỉnh ngôn ngữ và văn phong...',
      '✨ Đang hoàn thiện tài liệu cho bạn...',
    ],
    basic: [
      '⚡ Erix Life đang xử lý yêu cầu...',
      '🧠 Đang phân tích và tìm câu trả lời...',
      '🔍 Đang tra cứu thông tin liên quan...',
      '✨ Đang tổng hợp kết quả cho bạn...',
    ],
  };

  const msgs = messagesByMode[mode || 'basic'] || messagesByMode.basic;

  useEffect(() => {
    const dotTimer = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    const msgTimer = setInterval(() => {
      setMsgIdx(prev => (prev + 1) % msgs.length);
    }, 3000);
    return () => { clearInterval(dotTimer); clearInterval(msgTimer); };
  }, [msgs.length]);

  return (
    <div style={{ marginTop: '16px', padding: '12px 16px', background: 'rgba(99, 102, 241, 0.05)', borderRadius: '8px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '10px', border: '1px dashed rgba(99, 102, 241, 0.25)' }}>
      <div className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />
      <span style={{ fontSize: '12px', fontStyle: 'italic', transition: 'opacity 0.3s' }}>{msgs[msgIdx]}{dots}</span>
    </div>
  );
};

function SessionBubble({
  session,
  onEditPrompt,
  onSendToDocument,
  onRetry,
}: {
  session: ChatSession;
  onEditPrompt?: (p: string) => void;
  onSendToDocument?: (content: string, isWow?: boolean) => void;
  onRetry?: (s: ChatSession) => void;
}) {
  // Steps tư duy: tách lines bắt đầu bằng ​
  const filterContent = (text: string) => {
    const lines = text.split('\n');
    const steps = lines
      .filter(l => l.startsWith('​'))
      .map(l => l.replace('​', '').trim())
      .filter(Boolean);
    const contentText = lines
      .filter(l => !l.startsWith('​'))
      .join('\n')
      .trim();

    let thinkText = '';  // Giữ biến để tương thích nhưng không render
    let finalContent = contentText;

    // Xoá TOÀN BỘ các thẻ <think>...</think> (bao gồm cả đóng và chưa đóng) khỏi finalContent
    // Không hiển thị think content ra UI nữa — chỉ giữ các status steps.
    finalContent = finalContent.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
    if (finalContent.includes('<think>')) {
      // Bỏ phần còn lại từ <think> chưa đóng
      finalContent = finalContent.slice(0, finalContent.indexOf('<think>')).trim();
    }

    if (!finalContent.trim()) {
      // Khi AI đang suy nghĩ (chưa có output thực), KHÔNG hiển thị think — chỉ để trống
      // để Thinking Panel hiện spinner status steps thay thế.
      finalContent = '';
    }
    
    return { steps, content: finalContent.trim(), think: '' }; // Luôn trả về think='' để ẩn panel think
  };

  const { steps: allStreamSteps, content: streamContent, think: streamThink } = filterContent(session.streamText ?? '');
  const { content: finalContent, think: finalThink } = filterContent(session.finalResult ?? '');

  const [feedbackGiven, setFeedbackGiven] = useState<number | null>(null);
  const [thinkingCollapsed, setThinkingCollapsed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (session.state === 'DONE' || session.state === 'FAILED') {
      setThinkingCollapsed(true);
    }
  }, [session.state]);

  const handleCopy = async () => {
    try {
      const textToCopy = displayContent || '';
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy text', e);
    }
  };

  const handleFeedback = async (score: number) => {
    if (feedbackGiven !== null) return;
    setFeedbackGiven(score);
    try {
      await import('../api/client').then(m => m.api.submitFeedback(session.id, score));
    } catch (e) {
      console.error('Failed to submit feedback', e);
    }
  };

  const [visibleStepCount, setVisibleStepCount] = useState(0);

  useEffect(() => {
    if (session.state !== 'STREAMING') {
      setVisibleStepCount(0);
      return;
    }
    // Nếu đã có thêm step mới nhưng chưa hiện hết → hẹn giờ reveal thêm 1 step
    if (allStreamSteps.length > visibleStepCount) {
      // Giãn delay thêm 1s theo yêu cầu (từ 3.5s lên 4.5s)
      const delay = visibleStepCount === 0 ? 0 : 4500;
      const t = setTimeout(() => {
        setVisibleStepCount(prev => Math.min(prev + 1, allStreamSteps.length));
      }, delay);
      return () => clearTimeout(t);
    }
  }, [allStreamSteps.length, visibleStepCount, session.state]);

  const visibleSteps = allStreamSteps.slice(0, visibleStepCount);
  const showFormatted = session.state === 'DONE' || session.state === 'FAILED';
  let displayContent = showFormatted ? (finalContent || streamContent) : streamContent;
  
  // Tự động xoá block waiting nếu như đã có nội dung khác đằng sau nó (tức là đã chạy xong chờ đợi),
  // hoặc xoá toàn bộ nếu state đã là DONE.
  if (showFormatted) {
    displayContent = displayContent.replace(/```waiting\n```\s*/g, '');
  } else {
    displayContent = displayContent.replace(/```waiting\n```\s*(?=\S)/g, '');
  }
  
  const activeThink = showFormatted ? finalThink : streamThink;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* USER PROMPT */}
      <div style={{ alignSelf: 'flex-end', maxWidth: '75%', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
        <div style={{ padding: '10px 14px',
          background: 'linear-gradient(135deg, #7c3aed, #06b6d4)',
          borderRadius: '14px 14px 4px 14px', fontSize: '13px', lineHeight: 1.6,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#fff' }}>
          {session.prompt.replace(/\[SYSTEM OVERRIDE INSTRUCTION[\s\S]*?\[\/SYSTEM OVERRIDE INSTRUCTION\]\n*/i, "").replace(/\[SYSTEM OVERRIDE INSTRUCTION:[\s\S]*?\]\n*/, "")}
        </div>
        {(showFormatted) && onEditPrompt && (
          <button 
            onClick={() => onEditPrompt(session.prompt)}
            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontFamily: 'var(--font-sans)', transition: 'color 0.2s', marginTop: '-2px' }}
            onMouseOver={e => e.currentTarget.style.color = '#fff'}
            onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
            title="Sửa lại prompt này"
          >
            ✏️ Edit
          </button>
        )}
      </div>

      {/* ASSISTANT BUBBLE */}
      {(session.state === 'STREAMING' || showFormatted) && (
        <div style={{ alignSelf: 'flex-start', maxWidth: '90%', width: '90%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          
          {/* Thinking panel - chi hien khi co status steps */}
          {visibleSteps.length > 0 && (
            <div style={{
              background: 'rgba(99, 102, 241, 0.04)',
              border: '1px solid rgba(99, 102, 241, 0.12)',
              borderRadius: '10px',
              padding: '10px 14px',
              marginBottom: '4px',
            }}>
              <div style={{
                fontSize: '11px', fontWeight: 700, color: '#818cf8',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', gap: '8px',
                cursor: 'pointer',
                userSelect: 'none',
                padding: '2px 0'
              }} onClick={() => setThinkingCollapsed(!thinkingCollapsed)}>
                {session.state === 'STREAMING' && <div className="spinner" style={{ width: 8, height: 8 }} />}
                <span>{session.state === 'STREAMING' ? 'Erix Life đang suy nghĩ...' : 'Quá trình suy nghĩ'}</span>
                <span style={{ fontSize: '10px', marginLeft: 'auto', color: 'rgba(129, 140, 248, 0.65)' }}>
                  {thinkingCollapsed ? '▶ Xem chi tiết' : '▼ Thu gọn'}
                </span>
              </div>
              
              {!thinkingCollapsed && (
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {visibleSteps.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '8px' }}>
                      {visibleSteps.map((step, i) => {
                        const isDone = step.startsWith('✅') || step.startsWith('✓');
                        const text = step.replace(/^[✅✓\s]+/, '').trim();
                        const isLatest = i === visibleSteps.length - 1 && !isDone;
                        return (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'flex-start', gap: '6px',
                            fontSize: '11px', fontFamily: 'var(--font-mono)', lineHeight: 1.5,
                            color: isDone ? 'rgba(16,185,129,0.9)' : isLatest ? 'rgba(200,210,230,0.9)' : 'rgba(148,163,184,0.7)',
                            animation: 'fadeInUp 0.35s ease backwards',
                          }}>
                            {isDone ? <span style={{ color: '#10b981', flexShrink: 0 }}>✓</span> : isLatest ? <div className="spinner" style={{ width: 8, height: 8, flexShrink: 0, marginTop: '2px' }} /> : <span style={{ color: '#10b981', flexShrink: 0 }}>✓</span>}
                            <span>{text}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Think content đã bị ẩn hoàn toàn — chỉ hiện status steps */}
                  {/* Không render activeThink để tránh hiển thị nội dung suy luận nội tâm của AI */}
                </div>
              )}
            </div>
          )}

          {/* Main Answer Content */}
          {displayContent && (
            <div className="markdown-body" style={{ fontSize: '13px', lineHeight: 1.7, whiteSpace: 'normal',
              wordBreak: 'break-word', color: 'var(--text-primary)' }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code(props: any) {
                    const { node, className, children, ...rest } = props;
                    const match = /language-(\w+)/.exec(className || '');
                    const codeStr = String(children).replace(/\n$/, '');
                    if (match) {
                      const lang = match[1];
                      if (lang === 'document' || lang === 'excel') {
                        return <DocumentBlock code={codeStr} lang={lang} onSendToDocument={onSendToDocument} />;
                      }
                      if (lang === 'waiting') {
                        return <AnimatedWaiting mode={session.mode} />;
                      }
                      return <CodeBlock code={codeStr} lang={lang} onSendToDocument={onSendToDocument} />;
                    } else if (codeStr.includes('\n')) {
                      return <CodeBlock code={codeStr} lang="" onSendToDocument={onSendToDocument} />;
                    }
                    return <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 4px', borderRadius: '4px', fontSize: '90%', fontFamily: 'var(--font-mono)' }} {...rest}>{children}</code>;
                  }
                }}
              >
                {displayContent + (!showFormatted && session.state === 'STREAMING' ? ' ▊' : '')}
              </ReactMarkdown>
            </div>
          )}

          {/* Persistent Waiting Indicator for Long Streams */}
          {session.state === 'STREAMING' && displayContent && !displayContent.includes('```waiting') && (
            <AnimatedWaiting mode={session.mode} />
          )}

          {/* Waiting for first token */}
          {session.state === 'STREAMING' && !displayContent && visibleSteps.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '12px', color: 'var(--text-secondary)' }}>
              <div className="spinner" style={{ width: 14, height: 14 }} />
              <span>Swarm AI đang khởi động...</span>
            </div>
          )}
          {session.state === 'DONE' && !displayContent && visibleSteps.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '12px', color: '#ef4444' }}>
              <span>⚠ AI không trả về phản hồi hoặc kết nối bị ngắt giữa chừng.</span>
            </div>
          )}
          
        </div>
      )}

      {/* FEEDBACK & COPY BUTTONS */}
      {showFormatted && displayContent && !session.error && (
        <div style={{ alignSelf: 'flex-start', marginLeft: '4px', marginTop: '4px', display: 'flex', gap: '8px' }}>
          <button 
            onClick={handleCopy}
            style={{ ...btnStyle, opacity: copied ? 1 : 0.6, color: copied ? '#10b981' : undefined }}
            title="Copy text"
          >
            {copied ? '✅' : '📋'}
          </button>
          {onSendToDocument && (
            <button 
              onClick={() => onSendToDocument(displayContent || '', true)}
              style={{ ...btnStyle, opacity: 0.8, color: 'var(--accent)' }}
              title="Đưa sang Wow Mode (Deep Expert) để xử lý chuyên sâu"
            >
              ⚡ /eric-wow
            </button>
          )}
          <button 
            onClick={() => handleFeedback(1)}
            disabled={feedbackGiven !== null}
            style={{ ...btnStyle, opacity: feedbackGiven === -1 ? 0.3 : 1, color: feedbackGiven === 1 ? '#10b981' : undefined }}
            title="Good response"
          >
            👍 {feedbackGiven === 1 && 'Cảm ơn'}
          </button>
          <button 
            onClick={() => handleFeedback(-1)}
            disabled={feedbackGiven !== null}
            style={{ ...btnStyle, opacity: feedbackGiven === 1 ? 0.3 : 1, color: feedbackGiven === -1 ? '#ef4444' : undefined }}
            title="Bad response"
          >
            👎 {feedbackGiven === -1 && 'Đã ghi nhận'}
          </button>
        </div>
      )}

      {session.error && (
        <div style={{
          alignSelf: 'flex-start', padding: '10px 14px',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px', color: '#f87171', fontSize: '12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px'
        }}>
          <span>⚠ {session.error}</span>
          {onRetry && (
            <button
              onClick={() => onRetry(session)}
              className="btn btn--secondary btn--sm"
              style={{
                fontSize: '11px', padding: '4px 10px',
                background: 'rgba(239,68,68,0.2)',
                borderColor: 'rgba(239,68,68,0.4)',
                color: '#fca5a5', cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
              title="Thử lại yêu cầu này"
            >
              🔄 Thử lại
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export type ChatMode = 'basic' | 'pro' | 'investigate' | 'research' | 'document';

export function ChatPage({ loadSessionId, hideHeader }: { loadSessionId?: string, hideHeader?: boolean }) {
  const { user } = useAuth();
  const [selectedMode, setSelectedMode] = useState<ChatMode>('basic');
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [allSessions, setAllSessions] = useState<ChatSession[]>([]);
  const sessions = useMemo(() => allSessions.filter(s => s.mode === selectedMode), [allSessions, selectedMode]);
  const [loading, setLoading]   = useState(false);
  const enableMemory = localStorage.getItem('enableMemory') === 'true';
  const endRef    = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef  = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const cortexWorkerRef = useRef<Worker | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(loadSessionId || null);

  useEffect(() => {
    setActiveSessionId(loadSessionId || null);
  }, [loadSessionId]);

  useEffect(() => {
    cortexWorkerRef.current = new Worker(new URL('../workers/cortex.worker.ts', import.meta.url), { type: 'module' });
    return () => cortexWorkerRef.current?.terminate();
  }, []);

  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (loadSessionId) {
      import('../api/client').then(m => {
        m.api.getSessionDetails(loadSessionId).then(data => {
          if (data && data.messages) {
            const newSessions: ChatSession[] = [];
            let i = 0;
            // The R2 format is [ {role:'user', content:''}, {role:'assistant', content:''} ]
            // Often preceded by a system message, but we just pair user + assistant
            const msgs = data.messages.filter((m: any) => m.role !== 'system');
            for (let j = 0; j < msgs.length; j++) {
              if (msgs[j].role === 'user') {
                const userMsg = msgs[j];
                const asstMsg = msgs[j+1]?.role === 'assistant' ? msgs[j+1] : null;
                newSessions.push({
                  id: `hist-${loadSessionId}-${j}`,
                  prompt: userMsg.content,
                  mode: selectedMode,
                  state: 'DONE',
                  pipelineState: 'completed',
                  planStream: '',
                  codeStream: '',
                  finalResult: asstMsg ? asstMsg.content : '',
                  streamText: '',
                });
                if (asstMsg) j++; // Skip the assistant message since we paired it
              }
            }
            setAllSessions(newSessions);
          }
        }).catch(e => console.error('Failed to load session:', e));
      });
    } else {
      setAllSessions(prev => prev.filter(s => s.mode !== selectedMode));
    }
  }, [loadSessionId, selectedMode]);

  // Auto scroll to bottom
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [sessions]);

  const autoHeight = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  };
  const updateSession = (id: string, updater: (s: ChatSession) => ChatSession) => {
    setAllSessions(prev => prev.map(s => s.id === id ? updater(s) : s));
  };

  const send = useCallback(async (text?: string, forceMode?: ChatMode) => {
    let prompt = (text ?? input).trim();
    const activeMode = forceMode || selectedMode;
    
    // Ghép slash command tự động nếu người dùng chọn mode mà chưa gõ lệnh
    if (activeMode === 'pro' && !prompt.startsWith('/pro') && !prompt.startsWith('/deep-thinking') && !prompt.startsWith('/chuyen-sau')) {
      prompt = `/pro ${prompt}`;
    } else if (activeMode === 'investigate' && !prompt.startsWith('/investigate')) {
      prompt = `/investigate-news ${prompt}`;
    } else if (activeMode === 'research' && !prompt.startsWith('/deep')) {
      prompt = `/deep-research ${prompt}`;
    } else if (activeMode === 'document' && !prompt.startsWith('/document') && !prompt.startsWith('/eric-wow')) {
      prompt = `/document ${prompt}`;
    }

    if (!prompt && selectedFiles.length === 0 || loading) return;
    
    // Check Guest Quota (5 queries / 4 hours)
    if (!user) {
      const now = Date.now();
      const fourHoursMs = 4 * 60 * 60 * 1000;
      let timestamps: number[] = [];
      try { timestamps = JSON.parse(localStorage.getItem('guest_queries') || '[]'); } catch (e) {}
      timestamps = timestamps.filter(t => now - t < fourHoursMs);
      if (timestamps.length >= 5) {
        alert('Bạn đã hết lượt dùng thử miễn phí (5 lượt / 4 giờ). Vui lòng Đăng ký tài khoản để tiếp tục trải nghiệm!');
        return;
      }
      timestamps.push(now);
      localStorage.setItem('guest_queries', JSON.stringify(timestamps));
    }

    setInput('');
    setLoading(true);
    if (inputRef.current) { inputRef.current.style.height = '44px'; }

    const token = getToken();
    // If we are continuing a session, use it. Otherwise create a new one.
    let sessionId = activeSessionId;
    if (!sessionId) {
      sessionId = `sess_${Date.now()}`;
      setActiveSessionId(sessionId);
    }
    
    // Upload files first
    let fileContext = '';
    const imagesBase64: string[] = [];
    if (selectedFiles.length > 0) {
      const { api } = await import('../api/client');
      const { extractTextFromFile } = await import('../utils/document-parser');
      
      for (const file of selectedFiles) {
        try {
          if (file.type.startsWith('image/')) {
            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            imagesBase64.push(base64);
            continue;
          }
          // Vẫn upload file để lưu R2 làm dấu mốc lịch sử
          const res = await api.uploadFile(file, sessionId);
          const extractedText = await extractTextFromFile(file);
          
          if (extractedText) {
            // Đẩy Text đã Parse (Local) vào Backend Queue Ingest để Sandbox & Llama3 siêu nén
            try {
              await fetch(`${API_BASE}/api/document/ingest`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                  textContent: extractedText,
                  fileName: file.name
                })
              });
              // Prompt siêu nhẹ, không tốn Token, báo AI tự tra cứu Vector DB
              fileContext += `\n\n[LƯU Ý HỆ THỐNG: Tài liệu đính kèm "${file.name}" đã được lọc rác và siêu nén thành Knowledge Graph, lưu trữ tại Não phụ Vector DB. AI hãy tra cứu não phụ nếu người dùng có câu hỏi liên quan đến tài liệu này!]`;
            } catch (err) {
               console.error('Ingest failed, using raw text fallback', err);
               fileContext += `\n\n--- [Bắt đầu tài liệu: ${file.name}] ---\n${extractedText}\n--- [Kết thúc] ---\n`;
            }
          } else {
            fileContext += `\n\n(Hệ thống chỉ tải lên file gốc "${file.name}" mà không trích xuất được văn bản).`;
          }
        } catch (e) {
          console.error('File upload/parse failed', e);
        }
      }
      setSelectedFiles([]);
    }

    let cortexContext = '';
    if (cortexWorkerRef.current) {
      try {
        cortexContext = await new Promise<string>((resolve) => {
          const msgId = Date.now().toString();
          const handler = (e: MessageEvent) => {
            if (e.data.messageId === msgId) {
              cortexWorkerRef.current?.removeEventListener('message', handler);
              resolve(e.data.result || '');
            }
          };
          cortexWorkerRef.current!.addEventListener('message', handler);
          cortexWorkerRef.current!.postMessage({ text: prompt, messageId: msgId });
          setTimeout(() => {
            cortexWorkerRef.current?.removeEventListener('message', handler);
            resolve('');
          }, 50); // Timeout cực ngắn 50ms để không làm khựng UX
        });
      } catch (err) {
        console.warn('Cortex Speculative Execution Failed:', err);
      }
    }

    let enrichedPrompt = prompt;
    if (cortexContext) {
      enrichedPrompt = `[CLIENT-CORTEX-MATH-RESULT: ${cortexContext}]\n\n` + prompt;
    }
    const finalPrompt = enrichedPrompt + fileContext;

    // Kiểm tra xem prompt người dùng có phải là lệnh tiếp tục / thử lại không
    const isContinuation = /^(tiếp tục|tiếp|tiếp đi|tiếp tục đi|nói tiếp|nói tiếp đi|viết tiếp|viết tiếp đi|giải thích tiếp|continue|go on|thử lại|retry)[.!?\s]*$/i.test(prompt.trim());
    const lastSession = sessions.length > 0 ? sessions[sessions.length - 1] : null;

    let targetPrompt = finalPrompt;

    if (isContinuation && lastSession) {
      if (lastSession.state === 'FAILED') {
        if (lastSession.finalResult && lastSession.finalResult.length > 30) {
          targetPrompt = `Tiếp tục hoàn tất câu trả lời cho yêu cầu sau (phần trước đã trả lời được một đoạn thì bị gián đoạn do lỗi mạng):\n\n[Yêu cầu]: "${lastSession.prompt}"\n\n[Đoạn đã hoàn thành]:\n${lastSession.finalResult}\n\n[Chỉ thị]: Hãy viết tiếp các phần còn lại cho đến khi hoàn chỉnh, không lặp lại đoạn đã hoàn thành trên.`;
        } else {
          targetPrompt = lastSession.prompt;
        }
      }
    }

    // Xây dựng lịch sử hội thoại từ các lượt chat trước
    const messagesHistory: Array<{ role: 'user' | 'assistant'; content: string }> = [];
    for (let i = 0; i < sessions.length; i++) {
      const s = sessions[i];
      // Nếu session trước bị FAILED và người dùng đang gửi lệnh tiếp tục/retry cho nó,
      // không đưa session FAILED rỗng vào history để tránh 2 tin nhắn User liên tiếp gây lệch ngữ cảnh
      if (s.state === 'FAILED' && i === sessions.length - 1 && isContinuation) {
        continue;
      }
      if (s.state === 'DONE') {
        messagesHistory.push({ role: 'user', content: s.prompt });
        if (s.finalResult) {
          messagesHistory.push({ role: 'assistant', content: s.finalResult });
        }
      } else if (s.state === 'FAILED' && s.finalResult) {
        messagesHistory.push({ role: 'user', content: s.prompt });
        messagesHistory.push({ role: 'assistant', content: s.finalResult });
      }
    }
    // Push new user prompt
    messagesHistory.push({ role: 'user', content: targetPrompt });

    // Bubble ID ensures each message has a unique UI identifier,
    // while sessionId is sent to the backend to maintain conversation context
    const bubbleId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const newSession: ChatSession = {
      id: bubbleId,
      prompt: isContinuation && lastSession?.state === 'FAILED' && (!lastSession.finalResult || lastSession.finalResult.length <= 30) ? lastSession.prompt : finalPrompt,
      mode: activeMode,
      state: 'STARTING',
      pipelineState: '',
      planStream: '',
      codeStream: '',
      finalResult: '',
      streamText: '```waiting\n```\n',
    };

    // ── Tự động tạo tên đoạn chat nếu là tin nhắn đầu tiên ──
    if (sessions.length === 0 && !loadSessionId) {
      const cleanPrompt = prompt.replace(/^\/[a-z-]+\s/i, '').trim(); // Remove slash commands
      if (cleanPrompt) {
        let title = cleanPrompt.substring(0, 40);
        if (cleanPrompt.length > 40) title += '...';
        import('../api/client').then(m => m.api.renameSession(sessionId, title).catch(console.error));
      }
    }

    // Add session to UI
    setAllSessions(prev => [...prev, newSession]);

    abortControllerRef.current = new AbortController();
    let accumulated = '```waiting\n```\n';   // text gốc nhận từ API

    try {
      const res = await fetch(`${API_BASE}/v1/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          model: !user ? 'eric-ai-fast' : 'eric-ai-verbose',
          messages: messagesHistory,
          stream: true,
          max_tokens: 4096,
          allow_memory: enableMemory,
          mode: activeMode,
          session_id: sessionId,
          images: imagesBase64.length > 0 ? imagesBase64 : undefined,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as any;
        throw new Error(err.error?.message ?? err.error ?? `HTTP ${res.status}`);
      }

      // ── SSE Streaming với RAF Typewriter ──────────────────────
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let displayed = '```waiting\n```\n';     // text hiện trên UI (đuổi theo)
      let rafId = 0;
      let streamDone = false;

      // Cập nhật state sang STREAMING
      setAllSessions(prev => prev.map(s => s.id === bubbleId
        ? { ...s, state: 'STREAMING' } : s));

      // RAF loop: cứ mỗi frame thêm vài ký tự vào UI
      const tick = () => {
        if (displayed.length < accumulated.length) {
          const lag = accumulated.length - displayed.length;
          const step = lag > 500 ? 50 : lag > 200 ? 20 : lag > 50 ? 5 : 2;
          displayed = accumulated.slice(0, displayed.length + step);
          // Update streamText trong session
          setAllSessions(prev => prev.map(s => s.id === bubbleId
            ? { ...s, streamText: displayed } : s));
        }
        if (!streamDone || displayed.length < accumulated.length) {
          rafId = requestAnimationFrame(tick);
        }
      };
      rafId = requestAnimationFrame(tick);

      // Đọc SSE từ server
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        
        let nlIndex;
        while ((nlIndex = buffer.indexOf('\n\n')) !== -1) {
          const message = buffer.slice(0, nlIndex);
          buffer = buffer.slice(nlIndex + 2);
          
          for (const line of message.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (raw === '[DONE]') {
              streamDone = true;
              continue;
            }
            try {
              const parsed = JSON.parse(raw);
              const delta = parsed?.choices?.[0]?.delta?.content ?? '';
              if (delta) {
                if (delta.includes('[CLEAR_UI]')) {
                  accumulated = delta.replace('[CLEAR_UI]', '');
                  displayed = accumulated;
                } else {
                  accumulated += delta;
                }
              }
            } catch (err) {
              console.warn('SSE JSON parse error:', err);
            }
          }
        }
      }
      streamDone = true;

      // ── FIX BUG 2: Chờ RAF reveal nốt ──────────────────────────
      // Tăng timeout động để tránh bị "bụp" xoá sạch trước khi typewriter đuổi kịp
      const maxWaitMs = Math.max(10000, accumulated.length * 15);
      await new Promise<void>(resolve => {
        let waited = 0;
        const waitTick = () => {
          waited += 16;
          if (displayed.length >= accumulated.length) {
            cancelAnimationFrame(rafId);
            resolve();
          } else if (waited > maxWaitMs) {
            // Timeout: force-reveal toàn bộ text rồi resolve
            displayed = accumulated;
            setAllSessions(prev => prev.map(s => s.id === bubbleId
              ? { ...s, streamText: displayed } : s));
            cancelAnimationFrame(rafId);
            resolve();
          } else {
            requestAnimationFrame(waitTick);
          }
        };
        requestAnimationFrame(waitTick);
      });

      // Chuyển sang DONE — giữ lại displayed text, không xoá ngay
      setAllSessions(prev => prev.map(s => s.id === bubbleId ? {
        ...s,
        state: 'DONE',
        finalResult: accumulated,   // dùng accumulated (full) để filter
        streamText: '',             // clear sau khi finalResult đã set
      } : s));

    } catch (e: any) {
      if (e.name === 'AbortError') {
        setAllSessions(prev => prev.map(s => s.id === bubbleId ? {
          ...s,
          state: 'DONE',
          finalResult: (s.streamText || s.finalResult) + '\n\n*(Đã dừng bởi người dùng)*',
          streamText: '',
        } : s));
      } else {
        const cleanAccumulated = accumulated.replace(/```waiting[\s\S]*?```/gi, '').trim();
        setAllSessions(prev => prev.map(s => s.id === bubbleId ? {
          ...s,
          state: 'FAILED',
          finalResult: cleanAccumulated,
          streamText: cleanAccumulated,
          error: e.message || 'Kết nối mạng bị gián đoạn (Network Error)',
        } : s));
      }
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
    }
  }, [input, loading, enableMemory, allSessions, selectedFiles, loadSessionId, selectedMode]);

  const handleRetry = useCallback((failedSession: ChatSession) => {
    // Xoá session bị lỗi mạng và gọi lại chính xác prompt đó
    setAllSessions(prev => prev.filter(s => s.id !== failedSession.id));
    send(failedSession.prompt, failedSession.mode as ChatMode);
  }, [send]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--header-h) - 48px)', gap: 0 }}>
      {/* ── Header ─── */}
      {!hideHeader && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 16px 0', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <h1 className="page-title">Erix Life Swarm</h1>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <div className="badge badge--emerald" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
              Swarm Engine Online
            </div>
            {sessions.length > 0 && (
              <button className="btn btn--secondary btn--sm" onClick={() => setAllSessions(prev => prev.filter(s => s.mode !== selectedMode))}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Messages ─── */}
      <div style={{
        flex: 1, overflowY: 'auto', minHeight: 0,
        display: 'flex', flexDirection: 'column', gap: '24px',
        padding: '16px',
        background: 'var(--bg-glass)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        marginBottom: '12px',
      }}>
        {sessions.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '40px' }}>
            <div className="swarm-core-container">
              <div className="swarm-core-ring ring-1"></div>
              <div className="swarm-core-ring ring-2"></div>
              <div className="swarm-core-ring ring-3"></div>
              <div className="swarm-core-center"></div>
            </div>
            <div style={{ fontSize: '18px', color: 'var(--text-secondary)', fontWeight: 500, textAlign: 'center', letterSpacing: '2px', animation: 'pulse-text 3s infinite' }}>
              Ask anything...
            </div>
          </div>
        )}

        {sessions.map((s: ChatSession) => (
          <SessionBubble 
            key={s.id} 
            session={s} 
            onRetry={handleRetry}
            onEditPrompt={(p) => { 
              setInput(p); 
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                  inputRef.current.style.height = 'auto';
                  inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 240) + 'px';
                }
              }, 10);
            }}
            onSendToDocument={(content, isWow) => {
                setSelectedMode('document');
                const preview = content.length > 4000 ? content.substring(0, 4000) + '...' : content;
                const cmd = isWow ? '/eric-wow' : '/document';
                const autoPrompt = `${cmd} [SYSTEM OVERRIDE INSTRUCTION: Hệ thống vừa chuyển đoạn văn bản sau cho bạn. Bạn KHÔNG cần soạn thảo/xử lý ngay. Hãy trả lời cực kỳ ngắn gọn (1 câu) xác nhận đã nhận, và hỏi người dùng: "Bạn có yêu cầu soạn thảo hay xử lý chuyên sâu gì với văn bản này không?"][/SYSTEM OVERRIDE INSTRUCTION]\n\n${preview}`;
                setInput(autoPrompt);
                setTimeout(() => {
                  send(autoPrompt, 'document');
                }, 100);
            }}
          />
        ))}

        <div ref={endRef} />
      </div>

      {/* ── Input ─── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
        {/* Mode Selector */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '4px', background: 'var(--bg-panel)', padding: '8px', borderRadius: '8px', border: '1px solid var(--border)' }}>
          {MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => {
                if (!user && mode.id !== 'basic') {
                  alert('Tính năng này tốn nhiều tài nguyên máy chủ. Vui lòng Đăng nhập/Đăng ký tài khoản để sử dụng!');
                  return;
                }
                setSelectedMode(mode.id as ChatMode);
              }}
              title={!user && mode.id !== 'basic' ? 'Cần Đăng nhập' : mode.tooltip}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: selectedMode === mode.id ? 600 : 500,
                border: selectedMode === mode.id ? '1px solid #6366f1' : '1px solid transparent',
                background: selectedMode === mode.id ? 'rgba(99,102,241,0.1)' : 'transparent',
                color: !user && mode.id !== 'basic' ? 'rgba(148,163,184,0.4)' : (selectedMode === mode.id ? '#818cf8' : 'var(--text-secondary)'),
                cursor: !user && mode.id !== 'basic' ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {mode.label} {!user && mode.id !== 'basic' && '🔒'}
            </button>
          ))}
          {MODES.find(m => m.id === selectedMode)?.desc && (
            <div style={{ width: '100%', fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', paddingLeft: '4px', marginTop: '4px' }}>
              {MODES.find(m => m.id === selectedMode)?.desc}
            </div>
          )}
        </div>
        {selectedFiles.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {selectedFiles.map((f, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: '4px', padding: '4px 8px', fontSize: '11px', color: 'var(--text-secondary)'
              }}>
                📎 {f.name} ({(f.size / 1024).toFixed(1)} KB)
                <button 
                  onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}
                  style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', marginLeft: '4px' }}
                >×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, position: 'relative', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <input 
              type="file" 
              multiple 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              onChange={(e) => {
                if (e.target.files) {
                  setSelectedFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                }
                // clear input so same file can be selected again
                e.target.value = '';
              }}
            />
            <button 
              className="btn btn--secondary" 
              style={{ padding: '0 12px', height: '44px' }}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              title="Đính kèm tài liệu (PDF, DOCX, Ảnh...)"
            >
              📎
            </button>
            <textarea
              ref={inputRef}
              className="textarea"
              value={input}
              onChange={e => { setInput(e.target.value); autoHeight(e.target); }}
              onKeyDown={handleKey}
              placeholder={loading ? 'Đang xử lý...' : `Bạn muốn làm gì? (Gõ / để dùng lệnh đặc biệt hoặc enter để gửi)`}
              disabled={loading}
              rows={1}
              style={{
                width: '100%', resize: 'none', minHeight: '44px', maxHeight: '240px',
                fontSize: '13px', lineHeight: 1.5, padding: '12px 50px 12px 14px',
                fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
              }}
            />
          </div>
          {loading ? (
            <button
              className="btn btn--danger"
              onClick={stopGeneration}
              style={{ height: '44px', minWidth: '72px', justifyContent: 'center', flexShrink: 0, borderRadius: '10px' }}
            >
              <span style={{ fontSize: '12px' }}>Stop ⏹</span>
            </button>
          ) : (
            <button
              className="btn btn--primary"
              onClick={() => send()}
              disabled={!input.trim()}
              style={{ height: '44px', minWidth: '72px', justifyContent: 'center', flexShrink: 0, borderRadius: '10px' }}
            >
              <span>Send ↵</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


